import { sendEventMetric } from "@expert/cloudwatch-metrics";
import { isKnownPartner } from "@expert/shared-types";
import { hashKey, useQuery } from "@tanstack/react-query";
import { useEffect, useEffectEvent, useRef } from "react";

import { getEligibility } from "../../../api/eligibility";
import { isInvalidMdnError } from "../../../api/eligibility.errors";
import { useEligibilityRequest } from "../../../hooks/useEligibilityRequest";
import { useSalesContext } from "../../../sales-context/SalesContext";
import { useProductOfferStore, useVerifiedCustomer } from "../../../stores";

const QUERY_KEY = "sales-eligibility";

export function useEligibility(isEnabled = true) {
    const { sessionId, partner, logger, analytics, taskType, taskData, apiClients, features } = useSalesContext();
    const customer = useVerifiedCustomer(sessionId);
    const hasProducts = useProductOfferStore((state) => (state.getProducts(sessionId)?.length ?? 0) > 0);
    const { buildRequest, callInputs } = useEligibilityRequest();
    // An unrecognized Connect/CSD code (e.g. `pvl_the_source`) would only earn a Zod 400 from the
    // API, which the panel surfaces as an EligibilityError; skip it so the expert sees no offers.
    // We skip rather than alias these codes in normalizePartner (pvl_the_source -> thesource):
    // whether those lines should sell that partner's catalog is a product call that hasn't been made.
    const isPartnerKnown = isKnownPartner(partner);

    const queryKey = [
        QUERY_KEY,
        sessionId,
        customer?.mdn,
        customer?.accountNumber,
        // NOTE: email is intentionally NOT in the queryKey. Adding it (PR #6121,
        // ported from legacy PR #6037) caused useEligibility to re-fetch when
        // setCustomer wrote email, racing useCollectCustomerInfo's call inside
        // pDebounce(eligibility, 500ms). The trailing-edge fire used
        // useEligibility's no-firstName/lastName body, which sales-domain
        // Object.assign'd over the session's name fields, breaking smart-link
        // generation. See RDESQ-2032 (legacy fix shipped in PR #6178).
        customer?.currentSubscriptions,
        // callDetails.CLIENT and PROTECTED_SALES ARE in the queryKey (unlike the fields above):
        // they're eligibilityAttributes inputs the backend uses to shape the response (e.g.
        // Verizon IBC/OFR primary-card treatment, VMP narrowing for protected states), and they
        // can legitimately arrive after the first eligibility check fires (channel-share data
        // lands after customer verification). Without them here, staleTime: Infinity means a
        // late value never triggers a refetch, so the backend never gets a chance to reshape
        // the offer for it.
        callInputs.client,
        callInputs.isCustomerInProtectedState,
        // Read synchronously from the Connect contact, so it never arrives late. It's here
        // because it is also an eligibilityAttributes input that shapes the response.
        callInputs.isVmpSalesEnabled,
        // In the key for the same reason: chat MDNs decrypt asynchronously, so the ANI-derived
        // value can flip after the first check and must refetch rather than stay cached.
        callInputs.isInternationalCaller,
        // C360 registers after the call starts, so the IDs can arrive after the first check. The API
        // re-sends the whole USP session on every check, so a late value has to refetch to reach USP.
        taskData.c360Interaction?.interactionId,
        taskData.c360Interaction?.interactionLineId,
    ];
    const queryKeyHash = hashKey(queryKey);
    // What the store's products were last fetched for. The store, not the query cache, decides whether
    // a check is needed: a remount finds its inputs already fetched and doesn't re-check.
    const fetchedKeyHash = useProductOfferStore((state) => state.eligibilityInputsKeyBySession[sessionId]?.key);

    const {
        data: eligibilityResult,
        error,
        isError,
        isLoading,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            logger.info("Checking eligibility");
            // getEligibility writes the products, cues and meta to the store; the fetched-inputs key is this
            // hook's, so it's written here.
            const receipt = await getEligibility(
                apiClients.sales,
                logger,
                analytics,
                buildRequest(),
                features.isUsingMovedDiscoveryAndEngagementQuestions,
            );
            useProductOfferStore.getState().setEligibilityInputsKey(sessionId, queryKeyHash, receipt.requestedAt);
            return receipt;
        },
        // Do NOT gate on `!hasProducts`: that made the query sticky for the whole session, so a
        // change to an eligibility input (mdn/account/subscriptions, all in the queryKey) never
        // refreshed the offers and the expert could enroll a stale/ineligible offer. The store's
        // fetched-inputs key gates instead: unchanged inputs don't re-check, changed ones do.
        // Also gated on an active voice/chat task. When a task completes and the session stays open
        // (customer hung up, or the expert is parked in "Callback Pending" before dialling back), the
        // host drops the task-scoped inputs — `ivrInfo` (so `vmpSalesEnabled`) and the C360 IDs — and
        // the key changes. A check fired from that gap mints quotes for the wrong inputs and
        // invalidates the ones the expert is working with, and the callback task would then re-check
        // again to restore them (#8070). Nothing can be sold without a task anyway (IntegratedFlow
        // renders nothing and useHandleSendLink rejects), and the session's `currentTask` is kept
        // through wrap-up, so this only skips checks that have no sale to serve.
        enabled:
            isEnabled &&
            isPartnerKnown &&
            !!customer?.mdn &&
            (taskType.isVoice || taskType.isChat) &&
            fetchedKeyHash !== queryKeyHash,
        // Every check invalidates the previous quotes, so a cached result is never fresh and never kept.
        staleTime: 0,
        gcTime: 0,
        // An invalid MDN is deterministic — retrying won't change the result, and the UI
        // handles it with an inline correction view. Other failures still get one retry.
        retry: (failureCount, queryError) => (isInvalidMdnError(queryError) ? false : failureCount < 1),
    });

    // Surface the cases where the eligibility query would have fired but a gate blocked it:
    // an unrecognized partner, or an empty customer.mdn (the FIOS BAN-flow failure mode:
    // BAN lookup didn't return an mdn and the agent left the form blank).
    const logGateBlock = useEffectEvent((reason: string) => {
        logger.warn({ partner, sessionId, reason }, "Eligibility query disabled");
    });
    // Warn at most once per session — the gate condition can toggle as the agent
    // works the panel, and we only want to flag the session, not every re-render.
    const gateBlockLoggedForSession = useRef<string | null>(null);
    useEffect(() => {
        if (!isEnabled || hasProducts || gateBlockLoggedForSession.current === sessionId) {
            return;
        }
        if (!isPartnerKnown) {
            gateBlockLoggedForSession.current = sessionId;
            logGateBlock("unknown partner");
            // Before this gate an unknown partner 400'd and paged via EligibilityError; keep the
            // skip countable so a partner dropped from `partners` doesn't fail silently.
            void sendEventMetric("EligibilitySkippedUnknownPartner", { partner });
        } else if (!customer?.mdn) {
            gateBlockLoggedForSession.current = sessionId;
            logGateBlock("missing customer.mdn");
        }
    }, [isEnabled, hasProducts, isPartnerKnown, customer?.mdn, sessionId]);

    return { eligibilityResult, error, isError, isLoading };
}
